import type { Option } from '@/modules/common/types/option.type.ts';
import { LEGAL_DATA_TYPES, LEGAL_ENTITY_TYPES } from '@/modules/counterparty/consts/client-type-groups.ts';
import type {
  CounterpartyEditFallbackLabels,
  CounterpartyEditFormData,
  CounterpartyEditOptions,
} from '@/modules/counterparty/counterparty-edit/types/counterparty-edit-form.type.ts';
import type {
  CounterpartyEditParams,
  CounterpartyInternal,
} from '@/modules/counterparty/types/counterparty-internal.type.ts';

export function hasLegalData(typeId: number | null | undefined) {
  return typeId != null && LEGAL_DATA_TYPES.has(typeId);
}

export function isLegalEntity(typeId: number | null | undefined) {
  return typeId != null && LEGAL_ENTITY_TYPES.has(typeId);
}

function findOption(options: Option<number>[], id: number | null, fallbackLabel?: string): Option<number> | null {
  if (id === null) {
    return null;
  }

  return options.find(({ code }) => code === id) ?? { code: id, label: fallbackLabel || String(id) };
}

export function createEditForm(
  snapshot: CounterpartyInternal,
  options: CounterpartyEditOptions,
  fallbackLabels: CounterpartyEditFallbackLabels = {}
): CounterpartyEditFormData {
  return {
    type: findOption(options.types, snapshot.typeId, fallbackLabels.type),
    category: snapshot.category,
    name: snapshot.name,
    legalForm: findOption(options.legalForms, snapshot.legalFormId, fallbackLabels.legalForm),
    inn: snapshot.inn,
    kpp: snapshot.kpp,
    ogrn: snapshot.ogrn,
    okpo: snapshot.okpo,
    okato: snapshot.okato,
    manager: findOption(options.managers, snapshot.managerId, fallbackLabels.manager),
    segment: findOption(options.segments, snapshot.segmentId, fallbackLabels.segment),
    region: findOption(options.regions, snapshot.regionId, fallbackLabels.region),
    city: snapshot.city ? { code: snapshot.city, label: snapshot.city } : null,
    code: snapshot.code,
  };
}

function requireCode(option: Option<number> | null, field: string) {
  if (!option) {
    throw new Error(`The counterparty ${field} is not defined.`);
  }

  return option.code;
}

export function toEditParams(snapshot: CounterpartyInternal, form: CounterpartyEditFormData): CounterpartyEditParams {
  const typeId = requireCode(form.type, 'type');
  const withLegalData = hasLegalData(typeId);
  const withLegalEntity = isLegalEntity(typeId);
  const legalValue = (value: string) => (withLegalData ? value.trim() : '');

  return {
    ...snapshot,
    typeId,
    category: form.category,
    name: form.name.trim(),
    legalFormId: withLegalEntity ? (form.legalForm?.code ?? null) : null,
    inn: legalValue(form.inn),
    kpp: withLegalEntity ? form.kpp.trim() : '',
    ogrn: legalValue(form.ogrn),
    okpo: withLegalEntity ? form.okpo.trim() : '',
    okato: withLegalEntity ? form.okato.trim() : '',
    managerId: requireCode(form.manager, 'manager'),
    segmentId: requireCode(form.segment, 'segment'),
    regionId: requireCode(form.region, 'region'),
    city: form.city?.label.trim() ?? '',
    code: form.code.trim(),
  };
}
